import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Table from "@mui/material/Table";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import {
  DragDropContext,
  Draggable,
  Droppable,
} from "react-beautiful-dnd";

import { TextField, TableFooter, Button } from "@mui/material";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ProConfirmation from "../../../utils/ProConfirmation";
import { generateRandomString } from "../../../Utils";
import PMSwitch from "../../../utils/PMSwitch";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import ChecklistTypeCustomFieldsEditor from "./ChecklistTypeCustomFieldsEditor";
import {
  CHECKLIST_RESPONSE_ACTION_KEYS,
  effectiveActionsForTemplateRow,
  listsEqualAsSets,
  normalizeActionKeys,
  toggleKeyInList,
} from "./checklistResponseConfig";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const DEFAULT_ROWS = [
  {
    title: "",
    description: "",
    guidance: "",
    documents_required: false,
    response_actions: null,
    input_fields: [],
    row_order: 0,
  },
];

export function itemsContentFingerprint(rows) {
  return JSON.stringify(
    (Array.isArray(rows) ? rows : []).map((row) => ({
      id: row.id,
      title: row.title,
      guidance: row.guidance,
      description: row.description,
      documents_required: row.documents_required,
      response_actions: row.response_actions,
      input_fields: row.input_fields,
      row_order: row.row_order,
    }))
  );
}

const orderItems = (rows, previousItems = []) => {
  if (!rows) return [];

  return rows.map((row, i) => {
    const prev = previousItems.find(
      (p) =>
        (row.id != null && p.id === row.id) ||
        (p.row_order != null && p.row_order === row.row_order)
    );
    const draggableId = prev?.draggableId || generateRandomString();

    return {
      ...row,
      row_order: i,
      draggableId,
      input_fields: Array.isArray(row.input_fields) ? row.input_fields : [],
    };
  });
};

const ChecklistTypeItemRow = React.memo(function ChecklistTypeItemRow({
  item,
  drag,
  editable,
  typeDefaultKeys,
  onItemRowChange,
  onSwitchChange,
  onFieldsChange,
  onToggleAction,
  onDelete,
  provided,
  snapshot,
  t,
}) {
  const rowEffective = effectiveActionsForTemplateRow(item, typeDefaultKeys);
  const guidanceValue = item?.guidance ?? item?.description ?? "";

  return (
    <div
      key={"div" + (item.draggableId || item.id || item.row_order)}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={clsx(
        "mt-4 flex gap-4 rounded-xl border border-gray-100 bg-gray-50/50 px-5 py-5 shadow-sm",
        snapshot.isDragging && "ring-2 ring-teal-200"
      )}
    >
      {drag && (
        <div className="pt-2 text-gray-400">
          <DragHandleIcon />
        </div>
      )}

      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {editable && (
            <PMSwitch
              label={t("quality.checklist.type.documentsRequired")}
              name="documents_required"
              checked={item?.documents_required}
              onChange={(e) => onSwitchChange(e, item)}
            />
          )}
          <p className="text-xs font-medium text-gray-500">
            {t("quality.checklist.type.pointLabel", {
              n: item.row_order + 1,
            })}
          </p>
        </div>

        <TextField
          disabled={!editable}
          label={t("quality.checklist.type.pointTitle")}
          variant="outlined"
          size="small"
          value={item?.title}
          fullWidth
          name="title"
          onChange={(e) => onItemRowChange(e, item)}
        />

        <TextField
          disabled={!editable}
          label={t("quality.checklist.type.guidanceLabel")}
          helperText={t("quality.checklist.type.guidanceHint")}
          variant="outlined"
          size="small"
          value={guidanceValue}
          fullWidth
          name="guidance"
          multiline
          minRows={3}
          onChange={(e) => onItemRowChange(e, item)}
        />

        <ChecklistTypeCustomFieldsEditor
          fields={item?.input_fields || []}
          editable={editable}
          onChange={(nextFields) => onFieldsChange(item, nextFields)}
        />

        <div>
          <p className="text-xs font-semibold text-gray-800">
            {t("quality.checklist.type.pointActions")}
          </p>
          <p className="text-xs text-gray-500">
            {t("quality.checklist.type.pointActionsHint")}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {CHECKLIST_RESPONSE_ACTION_KEYS.filter((k) =>
              typeDefaultKeys.includes(k)
            ).map((key) => {
              const active = rowEffective.includes(key);
              return (
                <button
                  type="button"
                  key={key}
                  disabled={!editable}
                  onClick={() => onToggleAction(item, key)}
                  className={clsx(
                    "rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                    active
                      ? "border-teal-600 bg-white text-teal-800 shadow-sm"
                      : "border-gray-200 bg-white text-gray-400 line-through",
                    !editable && "cursor-not-allowed opacity-60"
                  )}
                >
                  {t(`quality.checklist.actions.${key}`)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 pt-1">
        <button
          type="button"
          onClick={() => onDelete(item.row_order)}
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
});

export default function ChecklistTypeItemLines(props) {
  const { t } = useTranslation();
  const { setFieldValue, values, isLoading, name, drag, editable, type } =
    props;

  const fieldName = name || "items";
  const rows = values?.items || props?.items;
  const typeDefaultKeys = useMemo(
    () => normalizeActionKeys(values?.default_response_actions),
    [values?.default_response_actions]
  );

  const [items, setItems] = useState(() =>
    orderItems(rows ? rows : DEFAULT_ROWS)
  );
  const lastPushedFingerprint = useRef(itemsContentFingerprint(items));

  const valuesItemsFingerprint = useMemo(
    () => itemsContentFingerprint(values?.items),
    [values?.items]
  );

  useEffect(() => {
    if (values?.id == null) return;
    const source = values?.items;
    if (!Array.isArray(source)) return;
    if (lastPushedFingerprint.current === valuesItemsFingerprint) return;
    setItems((prev) =>
      orderItems(source.length ? source : DEFAULT_ROWS, prev)
    );
  }, [values?.id, valuesItemsFingerprint]);

  useEffect(() => {
    if (!setFieldValue) return;
    const fp = itemsContentFingerprint(items);
    if (fp === lastPushedFingerprint.current || fp === valuesItemsFingerprint) {
      lastPushedFingerprint.current = fp;
      return;
    }
    lastPushedFingerprint.current = fp;
    setFieldValue(fieldName, items);
  }, [items, fieldName, setFieldValue, valuesItemsFingerprint]);

  const addItemRow = () => {
    const newRow = {
      title: "",
      description: "",
      guidance: "",
      input_fields: [],
      response_actions: null,
      avvik: false,
      row_order: items.length,
      documents_required: false,
    };
    setItems(orderItems([...items, newRow]));
  };

  const onItemRowChange = useCallback((e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    setItems((prevItems) => {
      const next = [...prevItems];
      const current = next[data.row_order];
      if (!current) return prevItems;
      next[data.row_order] = {
        ...current,
        [e.target.name]: e.target.value || e.target.checked,
      };
      return next;
    });
  }, []);

  const switchChange = useCallback((e, data) => {
    setItems((prevItems) => {
      const next = [...prevItems];
      const current = next[data.row_order];
      if (!current) return prevItems;
      next[data.row_order] = {
        ...current,
        [e.target.name]: e.target.checked,
      };
      return next;
    });
  }, []);

  const onFieldsChange = useCallback((item, nextFields) => {
    setItems((prevItems) =>
      prevItems.map((prevItem) =>
        prevItem.row_order === item.row_order ||
        (item.id != null && prevItem.id === item.id)
          ? { ...prevItem, input_fields: nextFields }
          : prevItem
      )
    );
  }, []);

  const toggleRowResponseAction = useCallback(
    (row, actionKey) => {
      const prevEffective = effectiveActionsForTemplateRow(row, typeDefaultKeys);
      const toggled = toggleKeyInList(prevEffective, actionKey);
      const nextStored = listsEqualAsSets(toggled, typeDefaultKeys)
        ? null
        : toggled;
      setItems((prev) => {
        const next = [...prev];
        const idx = next.findIndex((it) => it.row_order === row.row_order);
        if (idx === -1) return prev;
        next[idx] = {
          ...next[idx],
          response_actions: nextStored,
        };
        return next;
      });
    },
    [typeDefaultKeys]
  );

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    if (selectedItemRow != null) {
      const newArray = items.filter(
        (item) => item.row_order !== selectedItemRow
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = useCallback((row_order) => {
    setSelectedItemRow(row_order);
    setDeleteConfirmationOpen(true);
  }, []);

  useEffect(() => {
    if (props.items && props.parentCallback) {
      props.parentCallback(items);
    }
  }, [items, props.parentCallback, props.items]);

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      <div className="flex flex-col ">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items?.map((item, index) => (
                  <Draggable
                    isDragDisabled={!drag}
                    key={"drag" + (item.draggableId || item.id || index)}
                    draggableId={String(
                      item.draggableId || item.id || `new-${index}`
                    )}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ChecklistTypeItemRow
                        item={item}
                        drag={drag}
                        editable={editable}
                        typeDefaultKeys={typeDefaultKeys}
                        onItemRowChange={onItemRowChange}
                        onSwitchChange={switchChange}
                        onFieldsChange={onFieldsChange}
                        onToggleAction={toggleRowResponseAction}
                        onDelete={deleteRow}
                        provided={provided}
                        snapshot={snapshot}
                        t={t}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <Table>
        <TableFooter>
          <div>
            <div colSpan={2}>
              <Button onClick={() => addItemRow()}>
                {t("quality.checklist.type.addRow")}
              </Button>
            </div>
          </div>
        </TableFooter>
      </Table>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("quality.checklist.type.deleteRowConfirm")}
        action="delete"
        module="order_item"
        yesCallback={() => handleDeleteRow(selectedItemRow)}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

ChecklistTypeItemLines.defaultProps = {
  name: "items",
  drag: true,
  delete: true,
  editable: true,
  type: "checklist",
};
